import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Business as IceIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { Facture, FactureStatus } from '../../types/facture';

interface FacturePreviewProps {
  facture: Facture;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onPrint?: () => void;
}

const FacturePreview: React.FC<FacturePreviewProps> = ({
  facture,
  open,
  onClose,
  onEdit,
  onPrint,
}) => {
  const isAcompte = facture.type === 'acompte';

  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureStatus.EN_COURS:
        return <Chip label="En Cours" color="warning" size="small" />;
      case FactureStatus.ENCAISSE:
        return <Chip label="Encaissé" color="success" size="small" />;
      case FactureStatus.IMPAYE:
        return <Chip label="Impayé" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              {isAcompte ? "Facture d'Acompte" : 'Facture'} {facture.reference}
            </Typography>
            {isAcompte && (
              <Chip label="Acompte" size="small" color="info" />
            )}
            {getStatusChip(facture.status)}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Header Info */}
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Informations Générales
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <DateIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(facture.date).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
          </Grid>
          {isAcompte && facture.acompte_percentage && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2">
                <strong>Pourcentage d'acompte:</strong> {Number(facture.acompte_percentage)}%
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Client & BL/Devis Info Box */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.08)',
            borderRadius: 1,
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
            {/* Client Info */}
            <Box flex={1}>
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                Informations du Client
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2">
                    <strong>{facture.client_name || 'N/A'}</strong>
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2" color="text.secondary">
                    ICE: {facture.client_ice || 'N/A'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2" color="text.secondary">
                    {facture.client_address || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />
            <Divider sx={{ display: { xs: 'block', md: 'none' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />

            {/* Right: BLs or Devis Info */}
            <Box flex={1}>
              {isAcompte ? (
                <>
                  <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                    Devis Lié
                  </Typography>
                  {facture.devis_reference ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="body2">
                        <strong>{facture.devis_reference}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pourcentage: {Number(facture.acompte_percentage || 0)}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun devis
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                    Bons de Livraison
                  </Typography>
                  {facture.bons_livraison && facture.bons_livraison.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {facture.bons_livraison.map(bl => (
                        <Typography key={bl.id} variant="body2">
                          <strong>{bl.reference}</strong>
                          {bl.bon_commande_number && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}— BC: {bl.bon_commande_number}
                            </Typography>
                          )}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun BL
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Items Table */}
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Articles
        </Typography>
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell sx={{ width: '40%' }}>Désignation</TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>Quantité</TableCell>
                <TableCell align="right" sx={{ width: '20%' }}>Prix Unitaire</TableCell>
                <TableCell align="right" sx={{ width: '25%' }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facture.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.designation}</TableCell>
                  <TableCell align="right">{Number(item.quantite).toLocaleString('fr-FR')}</TableCell>
                  <TableCell align="right">
                    {Number(item.prix_unitaire).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </TableCell>
                  <TableCell align="right">
                    {Number(item.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Remarque */}
        {facture.remarque && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Remarque
            </Typography>
            <Box sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 1, mb: 3 }}>
              <Typography variant="body2">{facture.remarque}</Typography>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Paramètres
            </Typography>
            <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">TVA:</Typography>
                <Typography variant="body2">{Number(facture.tva_rate)}%</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Remise:</Typography>
                <Typography variant="body2">{Number(facture.remise)}%</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Totaux
            </Typography>
            <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total HT:</Typography>
                <Typography variant="body2">
                  {Number(facture.total_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">TVA ({Number(facture.tva_rate)}%):</Typography>
                <Typography variant="body2">
                  {Number(facture.tva_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Remise:</Typography>
                <Typography variant="body2">
                  -{Number(facture.remise_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #4CAF50">
                <Typography variant="subtitle1" fontWeight={600}>Total T.T.C:</Typography>
                <Typography variant="subtitle1" fontWeight={600} color="#4CAF50">
                  {Number(facture.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              {facture.acompte_deductions && facture.acompte_deductions.length > 0 && (
                <>
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(156, 39, 176, 0.4)' }}>
                    {facture.acompte_deductions.map(d => (
                      <Box key={d.id} display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ color: '#9C27B0' }}>
                          Acompte {d.reference}:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9C27B0' }}>
                          -{Number(d.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={1.5} pt={1.5} borderTop="2px solid #9C27B0">
                    <Typography variant="subtitle1" fontWeight={600}>Net à payer:</Typography>
                    <Typography variant="subtitle1" fontWeight={600} color="#9C27B0">
                      {Number(facture.net_a_payer).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Created By */}
        {facture.user_name && (
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Créé par: {facture.user_name}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={onPrint}
          sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
        >
          Imprimer
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
        >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacturePreview;
